import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';

const AdminPanel = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    is_admin: false,
    is_active: true
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Fetch all authorized users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const email = user?.email;
      
      if (!email) {
        console.error('No email available in user object');
        setError('User email not available');
        setLoading(false);
        return;
      }
      
      // Fix URL construction to prevent double /api
      const url = apiUrl === '/api' 
        ? `/api/authorized-users?email=${encodeURIComponent(email)}` 
        : `${apiUrl}/authorized-users?email=${encodeURIComponent(email)}`;
      console.log('Fetching authorized users from:', url);
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setUsers(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching authorized users:', err);
      setError('Failed to load authorized users. You may not have admin privileges.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [getAccessTokenSilently]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        is_admin: user.is_admin,
        is_active: user.is_active
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        is_admin: false,
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'is_admin' || name === 'is_active' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = await getAccessTokenSilently();
      let response;
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const email = user?.email;
      
      if (!email) {
        console.error('No email available in user object');
        throw new Error('User email not available');
      }

      if (editingUser) {
        // Update existing user
        // Fix URL construction to prevent double /api
        const updateUrl = apiUrl === '/api' 
          ? `/api/authorized-users/${editingUser.id}?email=${encodeURIComponent(email)}` 
          : `${apiUrl}/authorized-users/${editingUser.id}?email=${encodeURIComponent(email)}`;
        console.log('Updating user at:', updateUrl);
        response = await fetch(updateUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
      } else {
        // Create new user
        // Fix URL construction to prevent double /api
        const createUrl = apiUrl === '/api' 
          ? `/api/authorized-users?email=${encodeURIComponent(email)}` 
          : `${apiUrl}/authorized-users?email=${encodeURIComponent(email)}`;
        console.log('Creating user at:', createUrl);
        response = await fetch(createUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! Status: ${response.status}`);
      }

      setSnackbar({
        open: true,
        message: editingUser ? 'User updated successfully' : 'User added successfully',
        severity: 'success'
      });
      
      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      console.error('Error saving user:', err);
      setSnackbar({
        open: true,
        message: `Error: ${err.message}`,
        severity: 'error'
      });
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = await getAccessTokenSilently();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const email = user?.email;
      
      if (!email) {
        console.error('No email available in user object');
        throw new Error('User email not available');
      }
      
      // Fix URL construction to prevent double /api
      const deleteUrl = apiUrl === '/api' 
        ? `/api/authorized-users/${userToDelete.id}?email=${encodeURIComponent(email)}` 
        : `${apiUrl}/authorized-users/${userToDelete.id}?email=${encodeURIComponent(email)}`;
      console.log('Deleting user at:', deleteUrl);
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! Status: ${response.status}`);
      }

      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success'
      });
      
      setDeleteConfirmOpen(false);
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setSnackbar({
        open: true,
        message: `Error: ${err.message}`,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Käyttöoikeuksien hallinta
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Lisää käyttäjä
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="authorized users table">
              <TableHead>
                <TableRow>
                  <TableCell>Sähköposti</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell>Aktiivinen</TableCell>
                  <TableCell>Luotu</TableCell>
                  <TableCell>Päivitetty</TableCell>
                  <TableCell>Toiminnot</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.is_admin ? 'Kyllä' : 'Ei'}</TableCell>
                    <TableCell>{user.is_active ? 'Kyllä' : 'Ei'}</TableCell>
                    <TableCell>{new Date(user.created_at).toLocaleDateString('fi-FI')}</TableCell>
                    <TableCell>{new Date(user.updated_at).toLocaleDateString('fi-FI')}</TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(user)}
                        size="small"
                        title="Muokkaa"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(user)}
                        size="small"
                        title="Poista"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Ei käyttäjiä
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editingUser ? 'Muokkaa käyttäjää' : 'Lisää uusi käyttäjä'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="email"
              label="Sähköposti"
              type="email"
              fullWidth
              variant="outlined"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_admin}
                  onChange={handleInputChange}
                  name="is_admin"
                  color="primary"
                />
              }
              label="Admin-oikeudet"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  name="is_active"
                  color="primary"
                />
              }
              label="Aktiivinen"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Peruuta</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingUser ? 'Tallenna' : 'Lisää'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Vahvista poisto</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Haluatko varmasti poistaa käyttäjän {userToDelete?.email}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Peruuta</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Poista
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPanel;
